import { Component, OnInit, viewChild, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, inject, input, effect, signal, computed, untracked } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { CreateCampaignLogicService } from '../../services/create-campaign-logic.service';
import { AnchorType, ApplicationEnum } from '../../../../models';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MultiInputComponent } from './multi-input/multi-input.component';
import { LutApplicationItem } from '../../model/lut.model';
import { combineLatest, filter, firstValueFrom, map, tap } from 'rxjs';
import { FacebookHelperService } from '../../services/facebook-helper.service';
import { TelegramHelperService } from '../../services/telegram-helper.service';
import { CreateCampaignApiService } from '../../services/create-campaign-api.service';
import { MakingWavesLoaderService } from '../../../shared/services';
import { SelectGroupDialogComponent } from './select-group-dialog/select-group-dialog.component';
import { GroupRestrictions, SelectGroupResult } from '../../model/get-groups-by-params.model';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatSelectionList, MatListModule } from '@angular/material/list';
import { FileLoadComponent } from './file-load/file-load.component';
import { TextFormComponent } from './text-form/text-form.component';
import { AnchorTypeComponent } from './anchor-type/anchor-type.component';

const MAX_GROUPS_PER_AVATAR = 5;

@Component({
    selector: 'post-message-form',
    templateUrl: './post-message-form.component.html',
    styleUrls: ['./post-message-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatSlideToggleModule, MatIconModule, MatButtonModule, MatMenuModule, MatTooltipModule, MatListModule, FileLoadComponent, TextFormComponent, AnchorTypeComponent, MultiInputComponent]
})
export class PostMessageFormComponent implements OnInit {

  // Signal-based inputs
  applicationItem = input<LutApplicationItem | undefined>(undefined);
  ownerUnitId = input<string | undefined>(undefined);
  regionId = input<string | undefined>(undefined);
  gender = input<string | undefined>(undefined);
  ageFrom = input<number | undefined>(undefined);
  ageTo = input<number | undefined>(undefined);

  private appItem$ = toObservable(this.applicationItem);
  private ownerUnitId$ = toObservable(this.ownerUnitId);
  private regionId$ = toObservable(this.regionId);

  constructor() {
    // Reset anchors list and group selection when application changes
    effect(() => {
      this.applicationItem();
      untracked(() => {
        this.updateAnchorsList();
        this.clearList();
      });
    });

    // Re-run form validators when application or available avatars change
    effect(() => {
      this.applicationItem();
      this.numOfAvailableTgAvatars();
      untracked(() => {
        this.engagementFormGroup?.updateValueAndValidity();
      });
    });
  }

  multiInput = viewChild(MultiInputComponent);
  groupList = viewChild<MatSelectionList>('urlList');

  public fbAvatarsCount = signal<number | null>(null);
  private destroyRef = inject(DestroyRef);

  public anchorsTypeList!: AnchorType[];
  public selectedAnchorType!: AnchorType;
  public engagementFormGroup: FormGroup = new FormGroup({});
  public readonly ApplicationEnum = ApplicationEnum;
  public isImage = false;
  public isVideo = false;
  public isText = false;
  public isGroups: boolean = false;
  public isNumbers: boolean = false;

  public tgAvatarsCount = signal(0);

  // Multi-group URL signals
  newGroupUrl = signal('');
  editingIndex = signal<number | null>(null);
  editingText = signal('');
  urlValidationError = signal<string | null>(null);

  // TGE group data
  groupExternalIdMap = signal<Map<string, string>>(new Map());
  groupRestrictionsMap = signal<Map<string, GroupRestrictions | null>>(new Map());

  private createCampaignService = inject(CreateCampaignLogicService);
  private facebookHelperService = inject(FacebookHelperService);
  private telegramHelperService = inject(TelegramHelperService);
  private apiService = inject(CreateCampaignApiService);
  private loaderService = inject(MakingWavesLoaderService);
  private cdr = inject(ChangeDetectorRef);
  private fb = inject(FormBuilder);
  public dialog = inject(MatDialog);

  numOfAvailableTgAvatars = toSignal(
    combineLatest([
      this.createCampaignService.getAvatarsRegion$(),
      this.appItem$,
      this.ownerUnitId$,
      this.regionId$
    ]).pipe(
      filter(([avatarsByRegion]) => avatarsByRegion != null),
      map(([avatarsByRegion, app, ownerUnitId, regionId]) => {
        const match = avatarsByRegion.find(r => r.application_id === app?.id && r.region_id === regionId && r.owner_unit_id === ownerUnitId);
        return match ? match.counter : 0;
      }),
      tap(count => this.tgAvatarsCount.set(count))
    ),
    { initialValue: 0 }
  );

  ngOnInit(): void {

    this.engagementFormGroup = this.fb.group(
      {
        imageFile: [],
        videoFile: [],
        text: [],
        groupUrls: [[], [this.fbGroupUrlsValidator]],
        anchorType: [null, this.tgAnchorTypeRequiredValidator],
        anchors: [[]]
      },
      {
        validators: [this.atLeastOneValidator, this.complexValidator()]
      }
    );

    this.engagementFormGroup.updateValueAndValidity();
  }



  private updateAnchorsList(): void {
    const appId = this.applicationItem()?.id;
    if (appId === ApplicationEnum.telegram || appId === ApplicationEnum.telegramBt) {
      this.anchorsTypeList = this.createCampaignService.getAnchorsTypeList();
    }
  }


  alert2 = computed(() => {
    const app = this.applicationItem();
    return "מס' הודעות פר אווטר (טקסט/תמונה/וידאו): " + app?.max_anchors_for_video + "/" + app?.max_anchors_for_image + "/" + app?.max_anchors_for_text;
  });

  alert3 = computed(() => {
    const availableAvatars = this.tgAvatarsCount();
    const app = this.applicationItem();
    const maxAnchorsForVideo = app?.max_anchors_for_video ?? 0;
    const maxAnchorsForImage = app?.max_anchors_for_image ?? 0;
    const maxAnchorsForText = app?.max_anchors_for_text ?? 0;
    return "סה''כ הודעות (טקסט/תמונה/וידאו): " + (availableAvatars * maxAnchorsForVideo) + "/" + (availableAvatars * maxAnchorsForImage) + "/" + (availableAvatars * maxAnchorsForText);
  });


  ngAfterViewInit() {
    this.engagementFormGroup.statusChanges.subscribe(status => {
    });

    Object.keys(this.engagementFormGroup.controls).forEach(key => {
      const control = this.engagementFormGroup.get(key);
      control?.statusChanges.subscribe(status => {
        console.log(`${key} status:`, status, 'errors:', control.errors);
      });
    });
  }


  anchorTypeChanged(e: AnchorType) {
    this.selectedAnchorType = e;
    this.updateFormValue('anchorType', this.selectedAnchorType.anchorType);
    this.updateFormValue('anchors', []);
    this.isGroups = this.selectedAnchorType.anchorType === 'group';
    this.isNumbers = !this.isGroups;
    if (this.multiInput()) {
      this.multiInput()!.updateValuesFromFileToList([]);
    }
    if (this.applicationItem()?.id === ApplicationEnum.telegramBt) {
      this.clearList();
      this.engagementFormGroup.updateValueAndValidity();
    }
  }


  updateAvatarsRegionValidator() {
    const control = this.engagementFormGroup.get('avatarsRegion');
    if (control) {
      control.setValue(null);
      control.updateValueAndValidity();
    }
  }

  changeToggleState(event: any) {
    const id = event.source.id;
    switch (id) {
      case 'img-toggle':
        event.checked ? this.isImage = true : this.isImage = false;
        this.updateFormValue('imageFile', null);
        break;
      case 'vid-toggle':
        event.checked ? this.isVideo = true : this.isVideo = false;
        this.updateFormValue('videoFile', null);
        break;
      case 'text-toggle':
        event.checked ? this.isText = true : this.isText = false;
        this.updateFormValue('text', null);
        break;
    }
    this.engagementFormGroup.updateValueAndValidity();
  }

  private updateFormValue(key: string, value: any) {
    this.engagementFormGroup.get(key)?.patchValue(value);
  }

  // --- Multi-group URL management ---

  private isValidGroupUrl(url: string): boolean {
    const appId = this.applicationItem()?.id;
    if (appId === ApplicationEnum.telegramBt) {
      return this.telegramHelperService.TG_GROUP_REGEX.test(url);
    }
    return this.facebookHelperService.COMBINED_FB_REGEX.test(url);
  }

  addGroupUrl() {
    const url = this.newGroupUrl().trim();
    if (!url) return;

    const control = this.engagementFormGroup.get('groupUrls');
    control?.markAsTouched();

    if (!this.isValidGroupUrl(url)) {
      this.urlValidationError.set('פורמט קישור לא תקין');
      return;
    }

    const currentList: string[] = control?.value || [];
    if (currentList.includes(url)) {
      this.urlValidationError.set('קישור זה כבר קיים ברשימה');
      return;
    }

    this.urlValidationError.set(null);
    control?.setValue([...currentList, url]);
    this.newGroupUrl.set('');
    this.cdr.markForCheck();
  }

  public openSelectGroupDialog(event?: MouseEvent): void {
    (event?.target as HTMLElement)?.closest('button')?.blur();
    const dialogRef = this.dialog.open(SelectGroupDialogComponent, {
      data: {
        application_id: this.applicationItem()?.id,
        region_id: this.regionId(),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.result === 'okay' && result.groups?.length > 0) {
        this.urlValidationError.set(null);
        const control = this.engagementFormGroup.get('groupUrls');
        const currentList: string[] = control?.value || [];
        const updatedRestrictionsMap = new Map(this.groupRestrictionsMap());
        const updatedExternalIdMap = new Map(this.groupExternalIdMap());

        for (const group of result.groups as SelectGroupResult[]) {
          const url = group.group_user?.trim();
          if (currentList.includes(url)) continue;

          currentList.push(url);
          updatedRestrictionsMap.set(url, group.restrictions ?? null);
          if (group.external_entity_id) {
            updatedExternalIdMap.set(url, group.external_entity_id);
          }
        }

        this.groupRestrictionsMap.set(updatedRestrictionsMap);
        this.groupExternalIdMap.set(updatedExternalIdMap);
        control?.setValue([...currentList]);
        this.engagementFormGroup.updateValueAndValidity();
        this.cdr.detectChanges();
      }
    });
  }

  async validateBeforeSend(): Promise<string[]> {
    const errors: string[] = [];
    const appId = this.applicationItem()?.id;

    if (appId === ApplicationEnum.telegramBt) {
      const anchorType = this.engagementFormGroup.get('anchorType')?.value;
      if (anchorType === 'group') {
        return this.validateGroupAnchors(this.engagementFormGroup.get('groupUrls')?.value || []);
      }
      return this.validateAvatarAvailability();
    }

    if (appId === ApplicationEnum.tiktok) {
      return this.validateAvatarAvailability();
    }

    const urls: string[] = this.engagementFormGroup.get('groupUrls')?.value || [];
    if (urls.length === 0) return errors;

    return this.validateGroupAnchors(urls);
  }

  private async validateGroupAnchors(urls: string[]): Promise<string[]> {
    const errors: string[] = [];
    if (urls.length === 0) return errors;

    const appId = this.applicationItem()?.id;
    const ownerUnitId = this.ownerUnitId();
    const regionId = this.regionId();
    if (!appId || !ownerUnitId || !regionId) return errors;

    const externalIdMap = this.groupExternalIdMap();

    const urlToExternalId = new Map<string, string>();
    for (const url of urls) {
      const storedId = externalIdMap.get(url);
      if (storedId) {
        urlToExternalId.set(url, storedId);
      } else if (appId === ApplicationEnum.telegramBt) {
        // TGE groups store the full URL as external_entity_id in the DB
        urlToExternalId.set(url, url);
      } else {
        const parsed = this.facebookHelperService.parseUrl(url);
        if (parsed.resourceType === 'group' && parsed.groupId) {
          urlToExternalId.set(url, parsed.groupId);
        }
      }
    }

    const externalIds = [...urlToExternalId.values()];
    if (externalIds.length === 0) return errors;

    this.loaderService.showLoader("מבצע בדיקת חוקיות...");
    try {
      const MIN_HOURS_SINCE_JOIN = 72;
      const counts = await firstValueFrom(
        this.apiService.countAvatarsInGroups$(appId, externalIds, ownerUnitId, regionId, this.gender(), this.ageFrom(), this.ageTo(), MIN_HOURS_SINCE_JOIN)
      ).catch(() => null);

      if (counts === null) {
        errors.push('שגיאה בבדיקת אוואטרים בקבוצות');
      } else {
        for (const [url, extId] of urlToExternalId.entries()) {
          const count = counts[extId] ?? 0;
          if (count === 0) {
            errors.push(`אין אוואטרים בקבוצה ${url}`);
          }
        }
      }

      // Backend assigns each avatar up to MAX_GROUPS_PER_AVATAR groups, so N groups need ceil(N/5) avatars.
      if (urls.length > MAX_GROUPS_PER_AVATAR) {
        const requiredAvatars = Math.ceil(urls.length / MAX_GROUPS_PER_AVATAR);
        const totalAvatars = this.numOfAvailableTgAvatars() ?? 0;
        if (totalAvatars < requiredAvatars) {
          errors.push(`נדרשים לפחות ${requiredAvatars} אוואטרים עבור ${urls.length} קבוצות (זמינים ${totalAvatars} בלבד)`);
        }
      }

      const restrictionErrors = this.getRestrictionErrors();
      errors.push(...restrictionErrors);
    } finally {
      this.loaderService.hideLoader();
    }

    return errors;
  }

  // Verify at least one avatar exists for the selected application, owner unit + region before sending.
  private async validateAvatarAvailability(): Promise<string[]> {
    const errors: string[] = [];
    const appId = this.applicationItem()?.id;
    const ownerUnitId = this.ownerUnitId();
    const regionId = this.regionId();
    if (!appId || !ownerUnitId || !regionId) return errors;

    this.loaderService.showLoader("בודק זמינות אוואטרים...");
    try {
      const count = await firstValueFrom(
        this.apiService.countAvatars$(appId, ownerUnitId, regionId, this.gender(), this.ageFrom(), this.ageTo())
      ).catch(() => null);

      if (count === null) {
        errors.push('שגיאה בבדיקת זמינות אוואטרים');
      } else if (count === 0) {
        errors.push('אין אוואטרים זמינים עבור היחידה והאזור שנבחרו');
      }
    } finally {
      this.loaderService.hideLoader();
    }

    return errors;
  }

  private getRestrictionErrors(): string[] {
    if (this.applicationItem()?.id !== ApplicationEnum.telegramBt) return [];

    const errors: string[] = [];
    const map = this.groupRestrictionsMap();

    for (const [groupUrl, restrictions] of map.entries()) {
      if (!restrictions) continue;

      if (restrictions.send_messages && this.isText) {
        errors.push(`לא ניתן לשלוח טקסט לקבוצה ${groupUrl}`);
      }
      if (restrictions.send_photos && this.isImage) {
        errors.push(`לא ניתן לשלוח תמונה לקבוצה ${groupUrl}`);
      }
      if (restrictions.send_videos && this.isVideo) {
        errors.push(`לא ניתן לשלוח וידאו לקבוצה ${groupUrl}`);
      }
    }

    return errors;
  }

  deleteSelected() {
    const selectedValue = this.groupList()?.selectedOptions.selected[0]?.value;
    if (selectedValue !== undefined) {
      const currentList = this.engagementFormGroup.get('groupUrls')?.value as string[];
      const newList = currentList.filter(item => item !== selectedValue);
      this.engagementFormGroup.get('groupUrls')?.setValue(newList);
      const updatedRestrictionsMap = new Map(this.groupRestrictionsMap());
      updatedRestrictionsMap.delete(selectedValue);
      this.groupRestrictionsMap.set(updatedRestrictionsMap);
      const updatedExternalIdMap = new Map(this.groupExternalIdMap());
      updatedExternalIdMap.delete(selectedValue);
      this.groupExternalIdMap.set(updatedExternalIdMap);
      this.engagementFormGroup.updateValueAndValidity();
    }
  }

  clearList() {
    this.engagementFormGroup.get('groupUrls')?.setValue([]);
    this.groupExternalIdMap.set(new Map());
    this.groupRestrictionsMap.set(new Map());
    this.engagementFormGroup.updateValueAndValidity();
  }

  editItem(index: number) {
    const list = this.engagementFormGroup.get('groupUrls')?.value || [];
    this.editingIndex.set(index);
    this.editingText.set(list[index]);
  }

  saveEdit(index: number) {
    if (this.editingIndex() === null) return;
    const trimmed = this.editingText().trim();
    if (trimmed && this.isValidGroupUrl(trimmed)) {
      const list = [...(this.engagementFormGroup.get('groupUrls')?.value || [])];
      list[index] = trimmed;
      this.engagementFormGroup.get('groupUrls')?.setValue(list);
    }
    this.editingIndex.set(null);
  }

  cancelEdit() {
    this.editingIndex.set(null);
  }

  onOptionClick(event: MouseEvent, index: number) {
    const target = event.target as HTMLElement;
    const isRadio = target.closest('.mdc-radio') || target.closest('mat-pseudo-checkbox') || target.closest('.mat-mdc-list-option-checkbox-before');
    if (!isRadio) {
      this.groupList()?.deselectAll();
    }
  }

  // --- Validators ---

  private atLeastOneValidator(control: AbstractControl): { [key: string]: boolean } | null {
    const imageFileControl = control.get('imageFile')?.value;
    const videoFileControl = control.get('videoFile')?.value;
    const textControl = control.get('text')?.value;
    let res = null;
    if ((!imageFileControl && !videoFileControl && !textControl) || (imageFileControl === null && videoFileControl === null && textControl === '')) {
      res = { atLeastOne: true };
    }
    return res;
  }

  fbGroupUrlsValidator = (control: AbstractControl): ValidationErrors | null => {
    const appId = this.applicationItem()?.id;
    if (appId === ApplicationEnum.facebook || (appId === ApplicationEnum.telegramBt && this.isGroups)) {
      const urls = control.value;
      if (!urls || urls.length === 0) {
        return { requiredGroupUrlsViolation: true };
      }
    }
    return null;
  }

  tgAnchorTypeRequiredValidator = (control: AbstractControl): ValidationErrors | null => {
    const anchorType = control.value;
    const appId = this.applicationItem()?.id;
    const isTgOrTge = appId === ApplicationEnum.telegram || appId === ApplicationEnum.telegramBt;
    if (isTgOrTge && !anchorType) {
      return {requiredAnchorTypeViolation: true}
    }
    return null;
  }


  complexValidator(): ValidatorFn {
      return (formGroup: AbstractControl): ValidationErrors | null => {
        const appId = this.applicationItem()?.id;

        if (!appId) {
          return { appNotSelected: true };
        }

        if (appId === ApplicationEnum.facebook || (appId === ApplicationEnum.telegramBt && this.isGroups)) {
            return null;
        } else {
          return this.tgAnchorsValidator(formGroup);
        }
      };
    }

  private tgAnchorsValidator(group: AbstractControl):  ValidationErrors | null {
    const anchors = group.get('anchors')?.value;
    const appId = this.applicationItem()?.id;
    const isTgOrTge = appId === ApplicationEnum.telegram || appId === ApplicationEnum.telegramBt;

    if (isTgOrTge) {
      if(anchors.length === 0) {
        return { zeroAnchorsViolation: true };
      }

      if (appId === ApplicationEnum.telegram) {
        const availableAvatars = this.tgAvatarsCount();
        const maxAnchorsForVideo = this.applicationItem()?.max_anchors_for_video ?? 0;
        const maxAnchorsForImage = this.applicationItem()?.max_anchors_for_image ?? 0;
        const maxAnchorsForText = this.applicationItem()?.max_anchors_for_text ?? 0;

        if( this.isText && anchors.length > availableAvatars * maxAnchorsForText) {
          return { tgAvatarsLimitViolation: true };
        }

        if( (this.isImage && anchors.length > availableAvatars * maxAnchorsForImage) ||
            (this.isVideo && anchors.length > availableAvatars * maxAnchorsForVideo)
          ) {
          return { tgMediaLimitViolation: true };
        }
      }
    }

    return null;
  }


}
